import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { CompanyCombobox } from './CompanyCombobox';
import type { Contact, CreateContactInput } from '../types';

interface ContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: Contact | null;
  onSave: (input: CreateContactInput) => Promise<void>;
  isSaving: boolean;
  /**
   * When set (e.g. opened from a company-detail page), the company is fixed to
   * this company and the picker is replaced by a read-only chip.
   */
  lockedCompany?: { id: string; name: string };
}

/**
 * Create/edit a CRM contact. Shared by the contacts list page and the
 * company-detail contacts panel. Includes a company picker so a contact is
 * linked to its company at create time (closes the "no company picker" gap).
 */
export function ContactModal({
  open, onOpenChange, contact, onSave, isSaving, lockedCompany,
}: ContactModalProps) {
  const { t } = useTranslation('crm');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [notes, setNotes] = useState('');
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);
  const [companyName, setCompanyName] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    if (contact) {
      setFirstName(contact.first_name);
      setLastName(contact.last_name);
      setEmail(contact.email || '');
      setPhone(contact.phone || '');
      setMobilePhone(contact.mobile_phone || '');
      setTitle(contact.title || '');
      setDepartment(contact.department || '');
      setNotes(contact.notes || '');
      setCompanyId(lockedCompany?.id ?? contact.company_id);
      setCompanyName(lockedCompany?.name ?? contact.company_name);
    } else {
      setFirstName(''); setLastName(''); setEmail(''); setPhone('');
      setMobilePhone(''); setTitle(''); setDepartment(''); setNotes('');
      setCompanyId(lockedCompany?.id);
      setCompanyName(lockedCompany?.name);
    }
  }, [open, contact, lockedCompany]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({
      firstName,
      lastName,
      companyId: companyId || undefined,
      email: email || undefined,
      phone: phone || undefined,
      mobilePhone: mobilePhone || undefined,
      title: title || undefined,
      department: department || undefined,
      notes: notes || undefined,
    });
  };

  const isEdit = !!contact;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('contacts.modal.titleEdit') : t('contacts.modal.titleCreate')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact-first-name">{t('contacts.modal.firstName')}</Label>
              <Input id="contact-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="contact-last-name">{t('contacts.modal.lastName')}</Label>
              <Input id="contact-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </div>
            <div className="col-span-2">
              <Label id="contact-company-label">{t('contacts.modal.company')}</Label>
              {lockedCompany ? (
                <div className="flex items-center gap-1.5 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate">{lockedCompany.name}</span>
                </div>
              ) : (
                <CompanyCombobox
                  aria-labelledby="contact-company-label"
                  value={companyId}
                  valueName={companyName}
                  onChange={(id, name) => { setCompanyId(id); setCompanyName(name); }}
                />
              )}
            </div>
            <div>
              <Label htmlFor="contact-email">{t('contacts.modal.email')}</Label>
              <Input id="contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="contact-phone">{t('contacts.modal.phone')}</Label>
              <Input id="contact-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="contact-mobile">{t('contacts.modal.mobile')}</Label>
              <Input id="contact-mobile" value={mobilePhone} onChange={(e) => setMobilePhone(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="contact-title">{t('contacts.modal.title')}</Label>
              <Input id="contact-title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label htmlFor="contact-department">{t('contacts.modal.department')}</Label>
              <Input id="contact-department" value={department} onChange={(e) => setDepartment(e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label htmlFor="contact-notes">{t('contacts.modal.notes')}</Label>
              <Textarea id="contact-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={!firstName.trim() || !lastName.trim() || isSaving}>
              {isSaving ? t('common.saving') : isEdit ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
